import { Component } from '@theme/component';

/**
 * M+G click-triggered megamenu (Ref-2) — desktop only.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} [triggers]
 * @property {HTMLElement[]} [panels]
 * @property {HTMLElement} [backdrop]
 *
 * @extends {Component<Refs>}
 */
export class MgMegamenu extends Component {
  /** @type {string | null} */
  #activePanel = null;

  /** @type {HTMLButtonElement | null} */
  #lastTrigger = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #hoverCloseTimer = null;

  /** Desktop fine pointers only — matches the 881px megamenu breakpoint. */
  #hoverMedia = window.matchMedia('(min-width: 881px) and (hover: hover) and (pointer: fine)');

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearHoverClose();
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  #clearHoverClose() {
    if (this.#hoverCloseTimer == null) return;
    clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = null;
  }

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (!this.#hoverMedia.matches) return;
    if (!(event.target instanceof Element)) return;

    this.#clearHoverClose();

    const item = event.target.closest('.mg-megamenu__item');
    if (!(item instanceof HTMLElement) || !this.contains(item)) return;

    const trigger = item.querySelector('[data-mg-panel-trigger]');
    if (!(trigger instanceof HTMLButtonElement)) {
      if (this.#activePanel) this.#close(true, false);
      return;
    }

    const panelId = trigger.dataset.mgPanelTrigger;
    if (!panelId || this.#activePanel === panelId) return;
    this.#open(panelId, trigger);
  };

  #onPointerLeave = () => {
    if (!this.#hoverMedia.matches || !this.#activePanel) return;
    this.#clearHoverClose();
    this.#hoverCloseTimer = setTimeout(() => {
      this.#hoverCloseTimer = null;
      this.#close(true, false);
    }, 150);
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const trigger = target.closest('[data-mg-panel-trigger]');

    if (trigger instanceof HTMLButtonElement && this.contains(trigger)) {
      event.preventDefault();
      const panelId = trigger.dataset.mgPanelTrigger;
      if (!panelId) return;

      if (this.#activePanel === panelId) {
        this.#close(true, true);
      } else {
        this.#open(panelId, trigger);
      }
      return;
    }

    if (target.closest('[data-mg-panel-close]') && this.contains(target)) {
      event.preventDefault();
      this.#close(true, true);
    }
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.#activePanel) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#close(true, false);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape' && this.#activePanel) {
      event.preventDefault();
      this.#close(true, true);
    }
  };

  /**
   * @param {string} panelId
   * @returns {HTMLElement | null}
   */
  #getPanel(panelId) {
    return (
      this.querySelector(`#${CSS.escape(panelId)}`) ||
      document.getElementById(panelId)
    );
  }

  /**
   * @param {string} panelId
   * @param {HTMLButtonElement} trigger
   */
  #open(panelId, trigger) {
    this.#close(false, false);

    this.#activePanel = panelId;
    this.#lastTrigger = trigger;
    this.dataset.activePanel = panelId;

    const panel = this.#getPanel(panelId);
    if (panel instanceof HTMLElement) {
      panel.hidden = false;
      panel.dataset.active = 'true';
    }

    if (this.refs.backdrop instanceof HTMLElement) {
      this.refs.backdrop.hidden = false;
      this.refs.backdrop.dataset.active = 'true';
    }

    for (const item of this.refs.triggers ?? []) {
      const isActive = item === trigger;
      item.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      item.dataset.active = isActive ? 'true' : 'false';
    }
  }

  /**
   * @param {boolean} [resetTriggers]
   * @param {boolean} [restoreFocus]
   */
  #close(resetTriggers = true, restoreFocus = false) {
    if (!this.#activePanel) return;

    const panel = this.#getPanel(this.#activePanel);
    if (panel instanceof HTMLElement) {
      panel.hidden = true;
      panel.dataset.active = 'false';
    }

    if (this.refs.backdrop instanceof HTMLElement) {
      this.refs.backdrop.hidden = true;
      this.refs.backdrop.dataset.active = 'false';
    }

    if (resetTriggers) {
      for (const item of this.refs.triggers ?? []) {
        item.setAttribute('aria-expanded', 'false');
        item.dataset.active = 'false';
      }
    }

    const triggerToRestore = this.#lastTrigger;
    delete this.dataset.activePanel;
    this.#activePanel = null;
    this.#lastTrigger = null;

    if (restoreFocus && triggerToRestore instanceof HTMLButtonElement) {
      triggerToRestore.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('mg-megamenu-component')) {
  customElements.define('mg-megamenu-component', MgMegamenu);
}
